class WLSHelpers {
  /**
   * Constructor for WLSHelpers utility class
   * @param {object} [config] - Configuration object
   * @param {string} [config.moneyFormat] - Money format string
   */
  constructor(config) {
    const themeFormat = (typeof window !== 'undefined' && window.theme && window.theme.moneyFormat) || '{{amount}}';

    this._moneyFormat = (config && config.moneyFormat) || themeFormat;
  }

  /**
   * Create custom event (internal helper method)
   * @param {string} name - Event name
   * @param {object} detail - Event detail data
   * @param {boolean} bubbles - Whether event bubbles
   * @returns {CustomEvent} - Custom event instance
   */
  _createCustomEvent(name, detail, bubbles) {
    try {
      return new CustomEvent(name, { detail, bubbles });
    } catch (_) {
      const evt = document.createEvent('CustomEvent');
      evt.initCustomEvent(name, bubbles, false, detail);
      return evt;
    }
  }

  /**
   * Default value helper (internal helper method)
   * @param {*} value - Value to check
   * @param {*} fallback - Fallback value
   * @returns {*} - Value or fallback
   */
  _defaultTo(value, fallback) {
    return value == null || value !== value ? fallback : value;
  }

  /**
   * Format number with delimiters (internal helper method)
   * @param {number} number - Number to format (in cents)
   * @param {number} precision - Decimal precision
   * @param {string} thousands - Thousands separator
   * @param {string} decimal - Decimal separator
   * @returns {string} - Formatted number string
   */
  _formatWithDelimiters(number, precision, thousands, decimal) {
    precision = this._defaultTo(precision, 2);
    thousands = this._defaultTo(thousands, ',');
    decimal = this._defaultTo(decimal, '.');

    if (isNaN(number) || number == null) return '0';

    const isNegative = Number(number) < 0;
    const absolute = Math.abs(Number(number));
    const fixed = (absolute / 100).toFixed(precision);
    const parts = fixed.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    const cents = parts[1] ? decimal + parts[1] : '';
    return (isNegative ? '-' : '') + dollars + cents;
  }

  /**
   * Debounce execution of a function until `wait` ms have passed since the last call
   * @param {Function} fn - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function} - Debounced function
   */
  debounce(fn, wait) {
    let t;
    return function debounced() {
      const ctx = this,
        args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  /**
   * Trigger a bubbling CustomEvent
   * @param {HTMLElement} element - Element to dispatch event from
   * @param {string} name - Event name
   * @param {object} [data] - Event data
   * @returns {void}
   */
  triggerEvent(element, name, data) {
    if (!element) return;
    element.dispatchEvent(this._createCustomEvent(name, data || {}, true));
  }

  /**
   * Trigger a non-bubbling CustomEvent
   * @param {HTMLElement} element - Element to dispatch event from
   * @param {string} name - Event name
   * @param {object} [data] - Event data
   * @returns {void}
   */
  triggerNonBubblingEvent(element, name, data) {
    if (!element) return;
    element.dispatchEvent(this._createCustomEvent(name, data || {}, false));
  }

  /**
   * Generate a UUIDv4-like ID
   * @returns {string} - Generated UUID
   */
  getGuid() {
    let d = new Date().getTime();
    let d2 =
      (typeof performance !== 'undefined' &&
        performance &&
        typeof performance.now === 'function' &&
        performance.now() * 1000) ||
      0;

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      let r = Math.random() * 16;
      if (d > 0) {
        r = (d + r) % 16 | 0;
        d = Math.floor(d / 16);
      } else {
        r = (d2 + r) % 16 | 0;
        d2 = Math.floor(d2 / 16);
      }
      return (c === 'x' ? r : (r & 0x7) | 0x8).toString(16);
    });
  }

  /**
   * Format value in cents according to the template
   * @param {number|string} cents - Value in cents
   * @param {string} [format] - Money format template
   * @returns {string} - Formatted money string
   */
  formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');

    const formatString = format || this._moneyFormat || '{{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = formatString.match(placeholderRegex);
    if (!match) return String(formatString);

    const token = match[1];

    switch (token) {
      case 'amount':
        return formatString.replace(placeholderRegex, this._formatWithDelimiters(cents, 2, ',', '.'));
      case 'amount_no_decimals':
        return formatString.replace(placeholderRegex, this._formatWithDelimiters(cents, 0, ',', '.'));
      case 'amount_with_comma_separator':
        return formatString.replace(placeholderRegex, this._formatWithDelimiters(cents, 2, '.', ','));
      case 'amount_no_decimals_with_comma_separator':
        return formatString.replace(placeholderRegex, this._formatWithDelimiters(cents, 0, '.', ','));
      case 'amount_with_apostrophe_separator':
        return formatString.replace(placeholderRegex, this._formatWithDelimiters(cents, 2, "'", '.'));
      default:
        return formatString.replace(placeholderRegex, this._formatWithDelimiters(cents, 2, ',', '.'));
    }
  }

  /**
   * Change the default money format for this instance
   * @param {string} moneyFormat - New money format template
   * @returns {void}
   */
  setMoneyFormat(moneyFormat) {
    this._moneyFormat = moneyFormat;
  }
}

window.WLSHelpers = new WLSHelpers();

// === Examples ===
// const h = window.WLSHelpers;
// const id = h.getGuid();
// const debounced = h.debounce(() => console.log('ok'), 250);
// h.triggerEvent(document.body, 'wls:ping', { id });
// const price = h.formatMoney(123456, '{{amount}}'); // "1.234,56"
